import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  Typography,
  FormControl,
  Autocomplete,
  TextField,
  CircularProgress,
  Paper,
  Alert,
} from "@mui/material";
import { FileDownload, Print } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import * as xlsx from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DynamicTable from "../../../components/lab-dynamic-table";
import { fetchProfileData } from "../../../redux/reducers/profileSlice";
import DateRangeSelector from "../../../components/DateRangeSelector";

interface TestCountRow {
  testId?: string | null;
  testName?: string;
  departmentId?: string | null;
  departmentName: string;
  count: number;
}

interface TestCountResponse {
  departments?: TestCountRow[];
  count?: number;
  testId?: string | null;
  testName?: string | null;
  departmentId?: string | null;
  departmentName?: string | null;
}

const APPROVAL_STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "Pending Perform", label: "Pending Perform" },
  { value: "Pending Approval", label: "Pending Approval" },
  { value: "Approved", label: "Approved" },
];

const filterLabelSx = {
  fontSize: "11px",
  fontWeight: 600,
  mb: 0.25,
  color: "#374151",
  whiteSpace: "nowrap",
};

const filterBoxSx = {
  width: "155px",
  minWidth: "155px",
  flexShrink: 0,
};

const filterFieldSx = {
  backgroundColor: "white",
  borderRadius: "6px",
  fontSize: "12px",
  "& .MuiSelect-select": { py: 0.75 },
};

const actionButtonSx = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "11px",
  borderRadius: "6px",
  minWidth: "72px",
  px: 1.25,
  py: 0.75,
  backgroundColor: "#077DD3",
  boxShadow: "0 2px 6px rgba(7, 125, 211, 0.25)",
  "&:hover": {
    backgroundColor: "#0668b3",
    boxShadow: "0 4px 10px rgba(7, 125, 211, 0.35)",
  },
};

const formatTestLabel = (test: any) => {
  const name = test?.testName || test?.displayName || "";
  const code = test?.testCode;
  if (code != null && code !== "" && name) {
    return `${code} - ${name}`;
  }
  return name;
};

const TestCountReport: React.FC = () => {
  const dispatch = useDispatch();
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const collectionCenters = profile?.data?.collectionCenter || [];

  const today = new Date().toISOString().split("T")[0];

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<TestCountRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const [dateRange, setDateRange] = useState("Today");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [selectedCollectionId, setSelectedCollectionId] = useState("all");
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  const [approvalStatus, setApprovalStatus] = useState("");
  const [selectedTest, setSelectedTest] = useState<any | null>(null);
  const [tests, setTests] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);

  const fetchDepartments = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/lab-department/getAllBySearch`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setDepartments(res.data?.data || []);
    } catch (error: any) {
      console.error("Error fetching departments:", error);
    }
  };

  const fetchTests = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/tests/getAllLabTestsBySearch`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (res.data?.isSuccess === true) {
        setTests(res.data?.data || []);
      }
    } catch (error: any) {
      console.error("Error fetching tests:", error);
    }
  };

  const fetchTestCountReport = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        fromDate,
        toDate,
      };

      if (selectedCollectionId !== "all") {
        params.collectionId = selectedCollectionId;
      }
      if (selectedDepartmentId) {
        params.departmentId = selectedDepartmentId;
      }
      if (approvalStatus) {
        params.approvalStatus = approvalStatus;
      }
      if (selectedTest?._id) {
        params.testId = selectedTest._id;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/dashboard/getTestToPerformCount`,
        {
          params,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.isSuccess) {
        const data: TestCountResponse = response.data.data;

        if (selectedTest?._id) {
          const row: TestCountRow = {
            testId: data.testId ?? selectedTest._id,
            testName:
              data.testName ||
              selectedTest.testName ||
              selectedTest.displayName ||
              "Unknown",
            departmentId: data.departmentId ?? null,
            departmentName: data.departmentName || "Unknown",
            count: data.count ?? 0,
          };
          setReportData([row]);
          setTotalCount(row.count);
        } else {
          const departmentsList = data.departments || [];
          setReportData(departmentsList);
          setTotalCount(
            departmentsList.reduce((sum, item) => sum + (item.count || 0), 0)
          );
        }
      } else {
        toast.error(response.data.message || "Failed to fetch test count report");
        setReportData([]);
        setTotalCount(0);
      }
    } catch (error: any) {
      console.error("Error fetching test count report:", error);
      toast.error(error?.response?.data?.message || "Error fetching test count report");
      setReportData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    fromDate,
    toDate,
    selectedCollectionId,
    selectedDepartmentId,
    approvalStatus,
    selectedTest,
  ]);

  useEffect(() => {
    dispatch(fetchProfileData() as any);
    fetchDepartments();
    fetchTests();
  }, [dispatch]);

  useEffect(() => {
    fetchTestCountReport();
  }, [fetchTestCountReport]);

  const tableData = reportData.map((item, index) => [
    index + 1,
    item.testName || "Unknown",
    item.departmentName || "Unknown",
    item.count,
  ]);

  if (reportData.length > 0) {
    tableData.push(["", "", "TOTAL", totalCount]);
  }

  const selectedDepartmentName =
    !selectedDepartmentId
      ? "All Departments"
      : departments.find((d: any) => d._id === selectedDepartmentId)
          ?.labDepartmentName || "—";

  const selectedApprovalLabel =
    APPROVAL_STATUS_OPTIONS.find(option => option.value === approvalStatus)?.label ||
    "All Statuses";

  const selectedTestName = selectedTest
    ? formatTestLabel(selectedTest)
    : "All Tests";

  const handleExcelExport = () => {
    if (!reportData.length) {
      toast.warn("No data to export.");
      return;
    }

    const headerRows = [
      ["Test Count Report"],
      [`Period: ${fromDate} to ${toDate}`],
      [`Department: ${selectedDepartmentName}`],
      [`Test: ${selectedTestName}`],
      [`Approval Status: ${selectedApprovalLabel}`],
      [`Date Limit: ${dateRange === "custom" ? "Custom" : dateRange}`],
      [],
      ["S.No", "Test Name", "Department", "Count"],
    ];

    const dataRows = [
      ...reportData.map((item, index) => [
        index + 1,
        item.testName || "Unknown",
        item.departmentName || "Unknown",
        item.count,
      ]),
      ["", "", "TOTAL", totalCount],
    ];

    const worksheet = xlsx.utils.aoa_to_sheet([...headerRows, ...dataRows]);
    worksheet["!cols"] = [
      { wch: 8 },
      { wch: 35 },
      { wch: 25 },
      { wch: 10 },
    ];

    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, "TestCountReport");
    xlsx.writeFile(workbook, `Test_Count_Report_${fromDate}_to_${toDate}.xlsx`);
  };

  const drawPdfFilterRow = (
    doc: jsPDF,
    label: string,
    value: string,
    x: number,
    y: number,
    labelWidth: number
  ) => {
    doc.setFont("helvetica", "bold").setTextColor(75, 85, 99);
    doc.text(label, x, y);
    doc.setFont("helvetica", "normal").setTextColor(31, 41, 55);
    doc.text(value, x + labelWidth, y);
  };

  const handlePrint = () => {
    if (!reportData.length) {
      toast.warn("No data to print.");
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    const contentWidth = pageWidth - margin * 2;
    const brandBlue: [number, number, number] = [7, 125, 211];
    let yPosition = margin;

    doc
      .setFontSize(16)
      .setFont("helvetica", "bold")
      .setTextColor(...brandBlue)
      .text("TEST COUNT REPORT", pageWidth / 2, yPosition, { align: "center" });

    yPosition += 5;
    doc
      .setFontSize(8)
      .setFont("helvetica", "normal")
      .setTextColor(107, 114, 128)
      .text("Laboratory Test-wise Count Summary", pageWidth / 2, yPosition, {
        align: "center",
      });

    yPosition += 4;
    doc
      .setDrawColor(...brandBlue)
      .setLineWidth(0.6)
      .line(margin, yPosition, pageWidth - margin, yPosition);

    const filterBoxY = yPosition + 4;
    const filterBoxHeight = 30;
    doc
      .setFillColor(248, 250, 252)
      .setDrawColor(229, 231, 235)
      .setLineWidth(0.3)
      .roundedRect(margin, filterBoxY, contentWidth, filterBoxHeight, 2, 2, "FD");

    const col1X = margin + 5;
    const col2X = margin + contentWidth / 2 + 2;
    const labelWidth = 34;
    const rowHeight = 5.5;
    let filterY = filterBoxY + 7;
    const dateLimitLabel = dateRange === "custom" ? "Custom" : dateRange;
    const generatedAt = new Date().toLocaleString();

    drawPdfFilterRow(
      doc,
      "Period:",
      `${fromDate} to ${toDate}`,
      col1X,
      filterY,
      labelWidth
    );
    drawPdfFilterRow(
      doc,
      "Department:",
      selectedDepartmentName,
      col2X,
      filterY,
      labelWidth
    );

    filterY += rowHeight;
    drawPdfFilterRow(doc, "Test:", selectedTestName, col1X, filterY, labelWidth);
    drawPdfFilterRow(
      doc,
      "Approval:",
      selectedApprovalLabel,
      col2X,
      filterY,
      labelWidth
    );

    filterY += rowHeight;
    drawPdfFilterRow(doc, "Date Limit:", dateLimitLabel, col1X, filterY, labelWidth);
    drawPdfFilterRow(doc, "Generated:", generatedAt, col2X, filterY, labelWidth);

    yPosition = filterBoxY + filterBoxHeight + 8;

    const head = [["S.No", "Test Name", "Department", "Count"]];
    const body = [
      ...reportData.map((item, index) => [
        String(index + 1),
        item.testName || "Unknown",
        item.departmentName || "Unknown",
        String(item.count),
      ]),
      ["", "", "TOTAL", String(totalCount)],
    ];

    autoTable(doc, {
      startY: yPosition,
      head,
      body,
      theme: "grid",
      headStyles: {
        fillColor: brandBlue,
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 9,
        halign: "center",
        valign: "middle",
        cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
        lineColor: [6, 110, 190],
        lineWidth: 0.2,
      },
      bodyStyles: {
        fontSize: 8.5,
        textColor: [31, 41, 55],
        cellPadding: { top: 2.5, right: 3, bottom: 2.5, left: 3 },
        lineColor: [229, 231, 235],
        lineWidth: 0.15,
        valign: "middle",
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 16 },
        1: { halign: "left" },
        2: { halign: "left", cellWidth: 45 },
        3: { halign: "center", cellWidth: 22, fontStyle: "bold" },
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251],
      },
      margin: { left: margin, right: margin, top: margin, bottom: 14 },
      didParseCell(data) {
        if (data.row.index === body.length - 1) {
          data.cell.styles.fillColor = brandBlue;
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.lineColor = [6, 110, 190];
        }
        if (data.section === "body" && data.column.index === 3 && data.row.index < body.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.textColor = [7, 125, 211];
        }
      },
      didDrawPage(data) {
        const pageSize = doc.internal.pageSize;
        const pageCount = doc.getNumberOfPages();
        doc.setFontSize(7.5);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(156, 163, 175);
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          pageSize.getWidth() / 2,
          pageSize.getHeight() - 8,
          { align: "center" }
        );
      },
    });

    doc.autoPrint();
    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);

    const cleanup = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
      }
      window.removeEventListener("focus", cleanup);
    };

    window.addEventListener("focus", cleanup);
    iframe.src = blobUrl;

    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (win) {
          win.focus();
          win.print();
        }
      } catch (error) {
        console.error("Error triggering print dialog:", error);
        cleanup();
      }
    };

    setTimeout(() => {
      try {
        const win = iframe.contentWindow;
        if (win && document.body.contains(iframe)) {
          win.focus();
          win.print();
        }
      } catch (error) {
        console.error("Fallback print attempt failed:", error);
        cleanup();
      }
    }, 1000);
  };

  return (
    <Box sx={{ p: 2 }}>
    

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<Print sx={{ fontSize: "16px !important" }} />}
            onClick={handlePrint}
            disabled={!reportData.length}
            sx={actionButtonSx}
          >
            Print
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownload sx={{ fontSize: "16px !important" }} />}
            onClick={handleExcelExport}
            disabled={!reportData.length}
            sx={actionButtonSx}
          >
            Excel
          </Button>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            ml: { xs: 0, md: "auto" },
            overflowX: "auto",
            pb: 0.5,
          }}
        >
        <Box sx={filterBoxSx}>
          <Typography sx={filterLabelSx}>Collection Center</Typography>
          <FormControl size="small" fullWidth>
            <Select
              className="custom-input"
              value={selectedCollectionId}
              onChange={e => setSelectedCollectionId(e.target.value)}
              sx={filterFieldSx}
            >
              <MenuItem value="all">All Centers</MenuItem>
              {collectionCenters.map((center: any) => (
                <MenuItem key={center._id} value={center._id}>
                  {center.collectionCenterName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={filterBoxSx}>
          <Typography sx={filterLabelSx}>Department</Typography>
          <FormControl size="small" fullWidth>
            <Select
              className="custom-input"
              value={selectedDepartmentId}
              onChange={e => setSelectedDepartmentId(e.target.value)}
              displayEmpty
              sx={filterFieldSx}
            >
              <MenuItem value="">All Departments</MenuItem>
              {departments.map((dept: any) => (
                <MenuItem key={dept._id} value={dept._id}>
                  {dept.labDepartmentName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={filterBoxSx}>
          <Typography sx={filterLabelSx}>Test</Typography>
          <Autocomplete
            className="custom-input"
            size="small"
            options={tests}
            getOptionLabel={(option: any) => formatTestLabel(option)}
            value={selectedTest}
            onChange={(_event, newValue) => setSelectedTest(newValue)}
            renderInput={params => (
              <TextField {...params} placeholder="All Tests" sx={filterFieldSx} />
            )}
            sx={{ "& .MuiInputBase-root": { py: 0 } }}
          />
        </Box>

        <Box sx={{ ...filterBoxSx, width: "145px", minWidth: "145px" }}>
          <Typography sx={filterLabelSx}>Approval Status</Typography>
          <FormControl size="small" fullWidth>
            <Select
              className="custom-input"
              value={approvalStatus}
              onChange={e => setApprovalStatus(e.target.value)}
              sx={filterFieldSx}
            >
              {APPROVAL_STATUS_OPTIONS.map(option => (
                <MenuItem key={option.value || "all"} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <DateRangeSelector
          preset="lab"
          compact
          dateRange={dateRange}
          fromDate={fromDate}
          toDate={toDate}
          onDateRangeChange={setDateRange}
          onFromDateChange={setFromDate}
          onToDateChange={setToDate}
        />
        </Box>
      </Box>

      {/* Table Section */}
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="280px"
          sx={{
            border: "1px solid #e5e7eb",
            borderRadius: "10px",
            backgroundColor: "#fff",
          }}
        >
          <CircularProgress size={36} sx={{ color: "#077DD3" }} />
        </Box>
      ) : !reportData.length ? (
        <Alert
          severity="info"
          sx={{
            borderRadius: "10px",
            border: "1px solid #bfdbfe",
            backgroundColor: "#eff6ff",
          }}
        >
          No test count data found for the selected filters.
        </Alert>
      ) : (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e5e7eb",
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              backgroundColor: "#f9fafb",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#374151" }}>
              Test-wise Count Report
            </Typography>
          </Box>
          <Box sx={{ p: 1, maxHeight: "55vh", overflow: "hidden" }}>
            <DynamicTable
              headers={["S.No", "Test Name", "Department", "Count"]}
              data={tableData}
              loading={loading}
              height="50vh"
              rowStyleCondition={rowIndex =>
                rowIndex === tableData.length - 1
                  ? {
                      sx: {
                        fontWeight: "bold",
                        backgroundColor: "#f0f7ff",
                        color: "#077DD3",
                      },
                      className: "",
                    }
                  : { sx: {}, className: "" }
              }
            />
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default TestCountReport;
